import { usePassportProvider } from "@/context/PassportProvider";
import {
  LARGE_VERTICAL_BREAKPOINT,
  useIsLandscapeDisplayMode,
} from "@/hooks/useIsLandscapeDisplayMode";
import { trackLegacyEvent } from "@analytics";
import { Body, Checkbox, Stack } from "@biom3/react";
import { type ChangeEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const MarketingConsentStatusKey = "im_marketing_consent_status";
export enum MarketingConsentStatus {
  OptedIn = "opted_in",
  Unsubscribed = "unsubscribed",
  Subscribed = "subscribed",
}

type MarketingConsentProps = {
  variant?: "concise";
};

// This list is not exhaustive of every single city/variant but covers major timezones in GDPR regions. It aims for broad inclusion.
const gdprTimezoneNames = [
  "Europe/Amsterdam", // Netherlands
  "Europe/Andorra", // Andorra
  "Europe/Athens", // Greece
  "Asia/Nicosia", // Cyprus (covers the whole island)
  "Europe/Belgrade", // Serbia, Bosnia and Herzegovina, North Macedonia, Montenegro, Kosovo
  "Europe/Berlin", // Germany, Denmark, Norway, Sweden, Switzerland, Austria, Czech Republic, Hungary, Poland, Slovakia, Liechtenstein, Luxembourg, Italy (some areas)
  "Europe/Bratislava", // Slovakia
  "Europe/Brussels", // Belgium
  "Europe/Bucharest", // Romania, Moldova
  "Europe/Budapest", // Hungary
  "Europe/Busingen", // Germany (Büsingen am Hochrhein)
  "Europe/Chisinau", // Moldova
  "Europe/Copenhagen", // Denmark
  "Europe/Dublin", // Ireland, UK (Isle of Man, Guernsey, Jersey)
  "Europe/Gibraltar", // Gibraltar (UK)
  "Europe/Guernsey", // Guernsey (UK)
  "Europe/Helsinki", // Finland, Estonia, Latvia, Lithuania
  "Europe/Isle_of_Man", // Isle of Man (UK)
  "Europe/Istanbul", // Turkey (European part - though GDPR applicability is complex for Turkey)
  "Europe/Jersey", // Jersey (UK)
  "Europe/Kaliningrad", // Russia (Kaliningrad Oblast - geographically Europe)
  "Europe/Kiev", // Ukraine (most parts)
  "Europe/Kirov", // Russia
  "Europe/Lisbon", // Portugal (mainland)
  "Atlantic/Madeira", // Portugal (Madeira)
  "Atlantic/Azores", // Portugal (Azores)
  "Europe/Ljubljana", // Slovenia
  "Europe/London", // United Kingdom
  "Europe/Luxembourg", // Luxembourg
  "Europe/Madrid", // Spain (mainland)
  "Africa/Ceuta", // Spain (Ceuta)
  "Atlantic/Canary", // Spain (Canary Islands)
  "Europe/Malta", // Malta
  "Europe/Mariehamn", // Finland (Åland Islands)
  "Europe/Minsk", // Belarus
  "Europe/Monaco", // Monaco
  "Europe/Moscow", // Russia (western parts)
  "Europe/Oslo", // Norway
  "Europe/Paris", // France
  "Europe/Podgorica", // Montenegro
  "Europe/Prague", // Czech Republic
  "Europe/Riga", // Latvia
  "Europe/Rome", // Italy, San Marino, Vatican City
  "Europe/Samara", // Russia
  "Europe/San_Marino", // San Marino
  "Europe/Sarajevo", // Bosnia and Herzegovina
  "Europe/Simferopol", // Ukraine (Crimea - disputed)
  "Europe/Skopje", // North Macedonia
  "Europe/Sofia", // Bulgaria
  "Europe/Stockholm", // Sweden
  "Europe/Tallinn", // Estonia
  "Europe/Tirane", // Albania
  "Europe/Tiraspol", // Moldova (Transnistria)
  "Europe/Ulyanovsk", // Russia
  "Europe/Uzhgorod", // Ukraine (western parts)
  "Europe/Vaduz", // Liechtenstein
  "Europe/Vatican", // Vatican City
  "Europe/Vienna", // Austria
  "Europe/Vilnius", // Lithuania
  "Europe/Volgograd", // Russia
  "Europe/Warsaw", // Poland
  "Europe/Zagreb", // Croatia
  "Europe/Zaporozhye", // Ukraine (eastern parts)
  "Europe/Zurich", // Switzerland

  // French Overseas Territories
  "America/Cayenne", // French Guiana
  "America/Guadeloupe", // Guadeloupe
  "America/Martinique", // Martinique
  "America/Miquelon", // Saint Pierre and Miquelon
  "Indian/Mayotte", // Mayotte
  "Indian/Reunion", // Réunion
  "Pacific/Noumea", // New Caledonia
  "Pacific/Tahiti", // French Polynesia
  "Pacific/Wallis", // Wallis and Futuna

  // Dutch Caribbean Territories
  "America/Aruba", // Aruba
  "America/Curacao", // Curaçao
  "America/Kralendijk", // Bonaire, Sint Eustatius and Saba (BES Islands)
  "America/Lower_Princes", // Sint Maarten
];

const MarketingConsent = ({ variant }: MarketingConsentProps) => {
  const { t } = useTranslation();
  const isLandscapeDisplayMode = useIsLandscapeDisplayMode(
    LARGE_VERTICAL_BREAKPOINT,
  );
  const pastLoginMarketingConsentStatus = localStorage.getItem(
    MarketingConsentStatusKey,
  );

  const { marketingConsentStatus, setMarketingConsentStatus } =
    usePassportProvider();

  const isLikelyGdprRegion = useMemo(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (userTimezone && gdprTimezoneNames.includes(userTimezone)) {
        return true;
      }
    } catch (error: unknown) {
      // report error
      console.error(error);
      trackLegacyEvent({
        screen: "Login",
        userJourney: "Login",
        action: "Error",
        control: "Screen",
        controlType: "Effect",
        extras: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
      return true; // Fallback to true to be safe
    }
    return false;
  }, []);

  if (pastLoginMarketingConsentStatus) return;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const status = isChecked
      ? isLikelyGdprRegion
        ? MarketingConsentStatus.OptedIn
        : MarketingConsentStatus.Unsubscribed
      : isLikelyGdprRegion
        ? MarketingConsentStatus.Unsubscribed
        : MarketingConsentStatus.OptedIn;

    setMarketingConsentStatus(status);
  };

  return (
    <Stack
      testId="marketing-consent"
      direction="row"
      gap="base.spacing.x2"
      sx={{
        pt: "base.spacing.x2",
        textAlign: "left",
      }}
      alignItems="start"
    >
      <Checkbox
        onChange={handleOnChange}
        id="marketingConsent"
        name="marketingConsent"
        checked={
          isLikelyGdprRegion
            ? marketingConsentStatus === MarketingConsentStatus.OptedIn
            : marketingConsentStatus === MarketingConsentStatus.Unsubscribed
        }
      />
      <Body
        size={isLandscapeDisplayMode ? "xxSmall" : "xSmall"}
        sx={{ color: "base.color.text.body.secondary" }}
      >
        <label htmlFor="marketingConsent">
          {variant === "concise"
            ? isLikelyGdprRegion
              ? "Opt in to receive emails from Immutable"
              : "Opt out of receiving emails from Immutable"
            : isLikelyGdprRegion
              ? t("marketing_consent_gdpr")
              : t("marketing_consent")}
        </label>
      </Body>
    </Stack>
  );
};

export default MarketingConsent;
